/**
 * html2canvas 1.4.1 parses only rgb()/rgba()/hsl()/hsla(), hex and named colours,
 * and throws on any other colour function - "Attempting to parse an unsupported
 * color function". Material 21's theme builds many of its colours with color-mix(),
 * which the browser reports back as color(srgb ...), so a single Material component
 * inside an exported element made the whole render fail: Copy to Clipboard, Export
 * as Image and the PPT export all stopped working after the migration.
 *
 * Pass this as html2canvas's `onclone`. It runs on the copy html2canvas renders from,
 * so the live page is untouched, and rewrites every colour html2canvas would choke on
 * as the rgba() the browser actually paints, read back off a 1x1 canvas.
 */

// The colour properties html2canvas reads off each element.
const COLOR_PROPERTIES = [
  'color',
  'background-color',
  'border-top-color',
  'border-right-color',
  'border-bottom-color',
  'border-left-color',
  'outline-color',
  'text-decoration-color',
  '-webkit-text-stroke-color',
  'box-shadow',
];

const UNSUPPORTED = /\b(?:color-mix|color|oklch|oklab|lch|lab|hwb)\(/;
// One function call, allowing one level of nested parentheses inside it.
const UNSUPPORTED_CALL = /\b(?:color-mix|color|oklch|oklab|lch|lab|hwb)\((?:[^()]|\([^()]*\))*\)/g;

export function normaliseColorsForHtml2canvas(clonedDoc: Document, clonedElement: HTMLElement): void {
  const view = clonedDoc.defaultView;
  const ctx = clonedDoc.createElement('canvas').getContext('2d', { willReadFrequently: true });
  if (!view || !ctx) return;

  const rgbaOf = new Map<string, string>();
  const toRgba = (colour: string): string => {
    let rgba = rgbaOf.get(colour);
    if (!rgba) {
      ctx.clearRect(0, 0, 1, 1);
      ctx.fillStyle = colour;
      ctx.fillRect(0, 0, 1, 1);
      const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
      rgba = `rgba(${r}, ${g}, ${b}, ${+(a / 255).toFixed(3)})`;
      rgbaOf.set(colour, rgba);
    }
    return rgba;
  };

  // html2canvas also reads the page background from <html> and <body>.
  const elements: Element[] = [clonedDoc.documentElement, clonedDoc.body, clonedElement,
    ...Array.from(clonedElement.querySelectorAll('*'))];
  for (const el of elements) {
    if (!el || !(el as HTMLElement).style) continue;
    const style = view.getComputedStyle(el);
    for (const prop of COLOR_PROPERTIES) {
      const value = style.getPropertyValue(prop);
      if (value && UNSUPPORTED.test(value)) {
        (el as HTMLElement).style.setProperty(prop, value.replace(UNSUPPORTED_CALL, toRgba), 'important');
      }
    }
  }
}
